import { RadioGroup as RadixRadioGroup, Tabs as RadixTabs } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';

/* ============================================================================
   Display primitives: surfaces, status, structure.
   ========================================================================== */

// ── Card ────────────────────────────────────────────────────────────────────

const cardVariants = cva('rounded-lg border transition-colors', {
  variants: {
    tone: {
      surface: 'border-border bg-surface',
      subtle: 'border-border-subtle bg-surface-subtle',
      inset: 'border-border bg-bg-subtle',
    },
    elevated: { true: 'shadow-sm', false: '' },
    interactive: {
      true: 'text-left hover:border-border-strong hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
      false: '',
    },
  },
  defaultVariants: { tone: 'surface', elevated: false, interactive: false },
});

export interface CardProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, tone, elevated, interactive, ...props },
  ref,
) {
  return <div ref={ref} className={cn(cardVariants({ tone, elevated, interactive }), className)} {...props} />;
});

// ── Badge ───────────────────────────────────────────────────────────────────

const badgeVariants = cva(
  'inline-flex shrink-0 items-center gap-1 rounded-full border font-medium [&_svg]:size-3',
  {
    variants: {
      tone: {
        neutral: 'border-border bg-surface-hover text-fg-secondary',
        accent: 'border-accent-border bg-accent-subtle text-accent-text',
        success: 'border-success-border bg-success-bg text-success',
        warning: 'border-warning-border bg-warning-bg text-warning',
        danger: 'border-danger-border bg-danger-bg text-danger',
        info: 'border-info-border bg-info-bg text-info',
        outline: 'border-border bg-transparent text-fg-muted',
      },
      size: {
        sm: 'px-1.5 py-px text-[0.625rem]',
        md: 'px-2 py-0.5 text-[0.6875rem]',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, size, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone, size }), className)} {...props} />;
}

/** A coloured dot + label — a status that reads at a glance and in a screenshot. */
export function StatusDot({
  tone = 'neutral',
  pulse,
  className,
}: {
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'accent';
  pulse?: boolean;
  className?: string;
}) {
  const color = {
    neutral: 'bg-fg-faint',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
    accent: 'bg-accent',
  }[tone];
  return (
    <span className={cn('relative flex size-1.5 shrink-0', className)} aria-hidden>
      {pulse && <span className={cn('absolute inset-0 animate-ping rounded-full opacity-60', color)} />}
      <span className={cn('relative size-1.5 rounded-full', color)} />
    </span>
  );
}

// ── Avatar ──────────────────────────────────────────────────────────────────

export function Avatar({
  name,
  size = 'md',
  className,
}: {
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const dim = { sm: 'size-6 text-[0.625rem]', md: 'size-7 text-xs', lg: 'size-9 text-sm' }[size];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-primary font-semibold text-primary-fg',
        dim,
        className,
      )}
      aria-hidden
    >
      {initial}
    </span>
  );
}

// ── Alert ───────────────────────────────────────────────────────────────────

const alertVariants = cva('flex gap-2.5 rounded-lg border p-3 text-[0.8125rem] leading-relaxed', {
  variants: {
    tone: {
      info: 'border-info-border bg-info-bg text-info',
      success: 'border-success-border bg-success-bg text-success',
      warning: 'border-warning-border bg-warning-bg text-warning',
      danger: 'border-danger-border bg-danger-bg text-danger',
      neutral: 'border-border bg-surface-subtle text-fg-secondary',
    },
  },
  defaultVariants: { tone: 'info' },
});

export interface AlertProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  icon?: LucideIcon;
  title?: ReactNode;
  actions?: ReactNode;
}

export function Alert({ className, tone, icon: Icon, title, actions, children, ...props }: AlertProps) {
  return (
    <div className={cn(alertVariants({ tone }), className)} {...props}>
      {Icon && <Icon className="mt-px size-4 shrink-0" aria-hidden />}
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {/* No opacity: dimming text over a tinted surface is how a compliant
            colour pair quietly stops being one. */}
        {children && <div className={cn(title && 'mt-0.5')}>{children}</div>}
        {actions && <div className="mt-2 flex flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  );
}

// ── Skeleton ────────────────────────────────────────────────────────────────

export function Skeleton({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <div className={cn('skeleton', className)} style={style} aria-hidden />;
}

/** A loading placeholder shaped like the content that will replace it. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)} aria-hidden>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className="h-3" style={{ width: i === lines - 1 ? '60%' : '100%' }} />
      ))}
    </div>
  );
}

// ── Empty state ─────────────────────────────────────────────────────────────

export interface EmptyStateProps {
  icon?: LucideIcon;
  /** What this area is. */
  title: string;
  /** Why it is empty and what to do about it. */
  description?: ReactNode;
  /** The recommended next action. */
  action?: ReactNode;
  secondaryAction?: ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Every empty state explains what the area is, why it is empty, and the one
 * thing worth doing next. "No data found" is not an empty state.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'md',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'sm' ? 'gap-2 p-6' : 'gap-3 p-10',
        className,
      )}
    >
      {Icon && (
        <span
          className={cn(
            'flex items-center justify-center rounded-xl border border-border-subtle bg-surface-subtle text-fg-faint',
            size === 'sm' ? 'size-9' : 'size-12',
          )}
          aria-hidden
        >
          <Icon className={size === 'sm' ? 'size-4' : 'size-5'} />
        </span>
      )}
      <div className="max-w-sm">
        <p className={cn('font-medium text-fg', size === 'sm' ? 'text-[0.8125rem]' : 'text-sm')}>{title}</p>
        {description && <p className="mt-1 text-xs leading-relaxed text-fg-muted">{description}</p>}
      </div>
      {(action || secondaryAction) && (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}

// ── Tabs ────────────────────────────────────────────────────────────────────

export const Tabs = RadixTabs.Root;

export const TabList = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof RadixTabs.List>>(
  function TabList({ className, ...props }, ref) {
    return (
      <RadixTabs.List
        ref={ref}
        className={cn('flex items-center gap-4 overflow-x-auto border-b border-border', className)}
        {...props}
      />
    );
  },
);

export const Tab = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof RadixTabs.Trigger> & { count?: number }
>(function Tab({ className, count, children, ...props }, ref) {
  return (
    <RadixTabs.Trigger
      ref={ref}
      className={cn(
        '-mb-px flex shrink-0 items-center gap-1.5 border-b-2 border-transparent py-2 text-[0.8125rem] font-medium text-fg-muted transition-colors',
        'hover:text-fg',
        'data-[state=active]:border-fg data-[state=active]:text-fg',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        className,
      )}
      {...props}
    >
      {children}
      {count != null && (
        <span className="rounded-full bg-surface-hover px-1.5 text-[0.625rem] font-semibold text-fg-muted tabular">
          {count}
        </span>
      )}
    </RadixTabs.Trigger>
  );
});

export const TabPanel = RadixTabs.Content;

export interface FilterTabsOption<T extends string> {
  value: T;
  label: ReactNode;
  count?: number;
}

export interface FilterTabsProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: ReadonlyArray<FilterTabsOption<T>>;
  'aria-label': string;
  className?: string;
}

/**
 * Tabs that filter ONE list, rather than switching between panels.
 *
 * It looks like a tab bar and behaves like a single-choice filter, which is what
 * it is. That distinction is not pedantry: a real `role="tab"` declares
 * `aria-controls` pointing at a tab panel, and using tabs for a list filter left
 * every trigger pointing at a panel that does not exist — an invalid-ARIA
 * failure a screen reader hits before anyone notices it visually. A radio group
 * says the true thing: pick one of these to narrow what is below.
 */
export function FilterTabs<T extends string>({
  value,
  onValueChange,
  options,
  className,
  ...props
}: FilterTabsProps<T>) {
  return (
    <RadixRadioGroup.Root
      value={value}
      onValueChange={(v) => onValueChange(v as T)}
      orientation="horizontal"
      loop
      className={cn('flex items-center gap-4 overflow-x-auto border-b border-border', className)}
      {...props}
    >
      {options.map((option) => (
        <RadixRadioGroup.Item
          key={option.value}
          value={option.value}
          className={cn(
            '-mb-px flex shrink-0 items-center gap-1.5 border-b-2 border-transparent py-2',
            'text-[0.8125rem] font-medium text-fg-muted transition-colors hover:text-fg',
            'data-[state=checked]:border-fg data-[state=checked]:text-fg',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
          )}
        >
          {option.label}
          {option.count != null && (
            <span className="rounded-full bg-surface-hover px-1.5 text-[0.625rem] font-semibold text-fg-muted tabular">
              {option.count}
            </span>
          )}
        </RadixRadioGroup.Item>
      ))}
    </RadixRadioGroup.Root>
  );
}

// ── Structure ───────────────────────────────────────────────────────────────

export interface PageHeaderProps {
  title: ReactNode;
  /** One line on what this surface is for. Omit when the title says it. */
  description?: ReactNode;
  /** Breadcrumb or back link, above the title. */
  eyebrow?: ReactNode;
  /** Page-level actions, right-aligned. The primary one goes last. */
  actions?: ReactNode;
  /** A row below the header — filters, tabs, a search field. */
  toolbar?: ReactNode;
  className?: string;
  /** Keep the header pinned while the page body scrolls. */
  sticky?: boolean;
}

export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
  toolbar,
  className,
  sticky,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'flex shrink-0 flex-col gap-3 border-b border-border bg-surface px-gutter py-3.5',
        sticky && 'sticky top-0 z-20',
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {eyebrow && <div className="mb-1 flex items-center gap-1 text-xs text-fg-muted">{eyebrow}</div>}
          <h1 className="truncate font-display text-base font-semibold text-fg">{title}</h1>
          {description && <p className="mt-0.5 text-xs text-fg-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {toolbar}
    </header>
  );
}

export function SectionHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mb-3 flex items-end justify-between gap-3', className)}>
      <div className="min-w-0">
        <h2 className="font-display text-[0.8125rem] font-semibold text-fg">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-fg-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Monospace key hint. */
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex h-4 min-w-4 items-center justify-center rounded border border-border bg-surface-hover px-1 font-mono text-[0.625rem] font-medium text-fg-muted',
        className,
      )}
    >
      {children}
    </kbd>
  );
}
